import { ARIA, CLASS_NAMES, EVENTS, SELECTORS, MISC } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Dropdown module
 */
export default class Dropdown extends MessageBus {
  /**
   * Constructor function of the Dropdown class. Must receive one arguments, a DOM
   * node representing a delivery ticket.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {object} element DOM node that is passed into the constructor
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} Dropdown A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.toggle = this.element.querySelector(
      `.${CLASS_NAMES.INTRODUCTION_LIST_TOGGLE}`
    );
    this.content = this.element.querySelector(
      `.${CLASS_NAMES.INTRODUCTION_LIST_DROPDOWN_CONTENT}`
    );
    this.items = this.element.querySelectorAll(
      `.${CLASS_NAMES.INTRODUCTION_LIST_DROPDOWN_ITEM}`
    );
    this.parent = this.element.parentElement;
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} Dropdown A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.toggleDropdownHandler = this.toggleDropdown.bind(this);
    this.closeDropdownFromOutsideHandler =
      this.closeDropdownFromOutside.bind(this);
    this.selectItemHandler = this.selectItem.bind(this);
    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} Dropdown A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (this.toggle) {
      this.toggle.addEventListener(EVENTS.CLICK, this.toggleDropdownHandler);
    }

    this.items.forEach((item) => {
      item.addEventListener(EVENTS.CLICK, this.selectItemHandler);
    });

    return this;
  }

  /**
   * closes from click not in dropdown
   * @param {Event} event - the event object
   * @chainable
   */
  toggleDropdown(event) {
    event.stopPropagation();
    const isOpen = this.element.classList.contains(CLASS_NAMES.OPEN);

    if (!isOpen) {
      this.openDropdown();
    } else {
      this.closeDropdown();
    }
  }

  /**
   * open from click not in dropdown
   * @chainable
   */
  openDropdown() {
    this.element.classList.add(CLASS_NAMES.OPEN);
    this.content.setAttribute(ARIA.HIDDEN, MISC.FALSE);
    this.toggle.setAttribute(ARIA.EXPANDED, MISC.TRUE);

    document.body.addEventListener(
      EVENTS.CLICK,
      this.closeDropdownFromOutsideHandler,
      true
    );
  }

  /**
   * closes dropdown
   * @chainable
   */
  closeDropdown() {
    this.element.classList.remove(CLASS_NAMES.OPEN);
    this.content.setAttribute(ARIA.HIDDEN, MISC.TRUE);
    this.toggle.setAttribute(ARIA.EXPANDED, MISC.FALSE);

    document.body.removeEventListener(
      EVENTS.CLICK,
      this.closeDropdownFromOutsideHandler,
      true
    );
  }

  /**
   * toggles dropdown
   * @param {Event} event - the event object
   * @chainable
   */
  closeDropdownFromOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown();
    }
  }

  /**
   * opens dropdown
   * @param {Event} event - the event object
   * @chainable
   */
  selectItem(event) {
    const selectedItem = event.target;
    const link = selectedItem.getAttribute(SELECTORS.DATA_VALUE);

    if (link) {
      window.location.href = link;
    }

    this.toggle.textContent = selectedItem.textContent;
    this.closeDropdown();
  }
}
